import { dependentKeyCompat } from '@ember/object/compat';
import Service, { type Registry as Services, service } from '@ember/service';
import { tKey } from 'ember-intl';
import {
  PRICING_MANAGER_INTERNAL_PRODUCT_NAME,
  PRODUCT_NAMES,
  type ProductName,
} from 'frontend-utils/utils/constants/constant-values';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithMarket } from 'lumen/services/hotel-and-market';
import { ROUTE_DICT } from 'lumen/utils/constants/route-constants';
import { TRIAL_INDICATION_LOCALSTORAGE } from 'lumen/utils/constants/trial-indication-constants';

interface TrialIndicationStorageItem {
  [key: string]: {
    [key: string]: {
      [key: string]: unknown;
    };
  };
}

export default class ProductStateService extends Service {
  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentProduct() {
    if (this.router.currentRoute !== null) {
      return this.getProductForRoute(this.router.currentRoute.name);
    }
    return undefined;
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get currentProductState() {
    if (this.currentProduct === undefined) {
      return undefined;
    }

    return this.getProductState(this.currentProduct);
  }

  @dependentKeyCompat
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hotelOrMarket() {
    return this.hotelAndMarketService.hotelOrMarket;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRateInsight() {
    return this.currentProduct === PRODUCT_NAMES.PRODUCT_RATE_INSIGHT;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subscriptionId() {
    return this.hotelAndMarketService.hotel
      ? `hotel_${this.hotelAndMarketService.hotel.id}`
      : `market_${(this.hotelAndMarketService as HotelAndMarketServiceWithMarket).market.id}`;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getLocalStorageItem() {
    const localStorageItem = this.localStorage.getItem(
      TRIAL_INDICATION_LOCALSTORAGE,
    );
    if (localStorageItem !== null) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const parseditem = JSON.parse(localStorageItem);
      if (
        this.currentProduct !== undefined &&
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        parseditem[this.currentProduct] !== undefined
      ) {
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          parseditem[this.currentProduct][this.subscriptionId] !== undefined
        ) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
          return parseditem[this.currentProduct][this.subscriptionId];
        }
      }
    }
    return undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getProductForRoute(routeName: string) {
    const routeGroupKey = Object.keys(ROUTE_DICT).find(
      (route) => routeName.indexOf(route) !== -1,
    ) as keyof typeof ROUTE_DICT | undefined;
    if (!routeGroupKey) {
      return undefined;
    }
    const products = ROUTE_DICT[routeGroupKey];

    // Markets currently don't expose product states.
    // Since all market routes map to a single product we can simply return that one.
    if (this.hotelAndMarketService.market) {
      return products[0];
    }

    // Determine the most correct product for the current route.
    // Non-trials have priority over trials. If multiple trials apply, we take the longest one.
    let productToReturn:
      | ProductName
      | typeof PRICING_MANAGER_INTERNAL_PRODUCT_NAME = products[0];

    if (productToReturn === PRODUCT_NAMES.PRODUCT_PRICING_MANAGER) {
      productToReturn = PRICING_MANAGER_INTERNAL_PRODUCT_NAME;
    }

    let productState = this.hotelOrMarket.productState[productToReturn];

    for (let index = 1; index < products.length; index++) {
      const newProductToReturn = products[index] as ProductName;
      const newProductState =
        this.hotelOrMarket.productState[newProductToReturn];

      if (!productState && newProductState) {
        // It's possible it has no product state for it's first product. If it does have a product state for the new one just use that
        productToReturn = newProductToReturn;
        productState = newProductState;
      } else if (newProductState) {
        // If the new product is not a trial we will return it
        const isNotrial =
          productState.subscription_status === 'trial' &&
          newProductState.subscription_status !== 'trial';
        // Check if both have trial status
        const bothAreTrial =
          productState.subscription_status === 'trial' &&
          newProductState.subscription_status === 'trial';
        // Check if the new product has more days left then the previous
        const daysLeftisLarger =
          newProductState.days_left > productState.days_left;

        if (isNotrial || (bothAreTrial && daysLeftisLarger)) {
          productToReturn = newProductToReturn;
          productState = newProductState;
        }
      }
    }

    return productToReturn;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getProductState(
    product: ProductName | typeof PRICING_MANAGER_INTERNAL_PRODUCT_NAME,
  ) {
    // If there is no productState (currently not supported for markets, but will be in the future)
    // We return an empty object as default
    if (this.hotelOrMarket && this.hotelOrMarket.productState) {
      return this.hotelOrMarket.productState[product];
    }
    this.errorTracking.addBreadcrumb({
      message: 'No hotel or productstate was found',
    });

    return {};
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  hasSentTrialContactRequest() {
    try {
      let isCurrentSubscriptionFound = false;

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const localStorageItem = this.getLocalStorageItem();

      if (localStorageItem !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        isCurrentSubscriptionFound =
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          localStorageItem.contactRequest &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          localStorageItem.contactRequest.subscriptionId ===
            this.subscriptionId;
      }

      return isCurrentSubscriptionFound;
    } catch (error) {
      this.errorTracking.captureException(error);
      return false;
    }
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isRateInsightOnlyRoute(routeName: string) {
    return (
      this.getProductForRoute(routeName) === PRODUCT_NAMES.PRODUCT_RATE_INSIGHT
    );
  }

  sendTrialContactRequest(
    onSuccess: () => void,
    onAlreadyRequested: () => void,
    onError: () => void,
  ): void {
    if (
      this.hotelAndMarketService.hotel === undefined ||
      this.hotelAndMarketService.hotel === null
    ) {
      return;
    }

    const data = {
      subscription_id: this.hotelAndMarketService.hotel.id,
      product: this.currentProduct,
    };

    this.ajax
      .request('/self-service/trialcontactrequest/', {
        method: 'POST',
        dataType: 'json',
        contentType: 'application/json; charset=utf-8',
        data: JSON.stringify(data),
      })
      .then(() => {
        this.storeTrialContactRequestInLocalStorage();

        onSuccess();
      })
      .catch((response) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        if (response.payload) {
          this.errorTracking.addBreadcrumb({
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            message: response.payload.message,
          });
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          this.errorTracking.addBreadcrumb({ data: response });
        }

        // Possible API responses:
        // 400: Task already exists
        // 400: Subscription is a legacy subscription and is not in Salesforce -> The UM subscription is not linked to a valid SF account.
        // 403: User has no access -> permission denied
        // 403: Subscription is not a trial -> permission denied
        // 500: Salesforce failure
        if (
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          response?.payload &&
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          response.payload?.errors?.[0]?.code ===
            tKey('general.trial_indication.task_already_exists')
        ) {
          // In this case we'll also mark the contact request as sent. Avoid spamming the dialog.
          this.storeTrialContactRequestInLocalStorage();

          onAlreadyRequested();
        } else {
          onError();
        }
      });
  }

  setLocalStorageItem(key: string, value: unknown): void {
    if (this.currentProduct === undefined) {
      return;
    }

    const localStorageEntry = this.localStorage.getItem(
      TRIAL_INDICATION_LOCALSTORAGE,
    );
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const localStorageItem: TrialIndicationStorageItem | null =
      localStorageEntry === null ? null : JSON.parse(localStorageEntry);
    if (localStorageItem !== null) {
      const currentProduct = localStorageItem[this.currentProduct];
      if (currentProduct !== undefined) {
        const currentSubscription = currentProduct[this.subscriptionId];
        if (currentSubscription !== undefined) {
          currentSubscription[key] = value;
          this.localStorage.setItem(
            TRIAL_INDICATION_LOCALSTORAGE,
            JSON.stringify(localStorageItem),
          );
        } else {
          currentProduct[this.subscriptionId] = {
            [key]: value,
          };
          this.localStorage.setItem(
            TRIAL_INDICATION_LOCALSTORAGE,
            JSON.stringify(localStorageItem),
          );
        }
      } else {
        localStorageItem[this.currentProduct] = {
          [this.subscriptionId]: {
            [key]: value,
          },
        };
        this.localStorage.setItem(
          TRIAL_INDICATION_LOCALSTORAGE,
          JSON.stringify(localStorageItem),
        );
      }
    } else {
      const item: TrialIndicationStorageItem = {
        [this.currentProduct]: {
          [this.subscriptionId]: {
            [key]: value,
          },
        },
      };
      this.localStorage.setItem(
        TRIAL_INDICATION_LOCALSTORAGE,
        JSON.stringify(item),
      );
    }
  }

  storeTrialContactRequestInLocalStorage(): void {
    const contactRequest = {
      user: this.hotelAndMarketService.user.email,
      subscriptionId: this.subscriptionId,
    };
    // This function will store the contact request under the app-trial-indication-contact-request key in localStorage. That key is called with variable TRIAL_INDICATION_LOCALSTORAGE
    this.setLocalStorageItem('contactRequest', contactRequest);
  }
}

declare module '@ember/service' {
  interface Registry {
    'product-state': ProductStateService;
  }
}
